import React from 'react';
import { BRAND_COLORS, BRAND_LOGO } from '../../brand/tokens';
import { DISCREET_TITLE, useDiscreet } from '../../security/discreet';
import { useBrandName } from './useBrandName';
import { Wordmark } from './Wordmark';

export type LogoVariant = 'lockup' | 'wordmark' | 'icon';
/**
 * Which artwork to draw. `dark` (and `auto`) is the lockup for the app's dark
 * surfaces; `light` is for cream sections (the caller sets it, for example
 * inside `data-surface="cream"`). The wordmark is lipstick on both, the icon
 * is one tile.
 */
export type LogoTone = 'auto' | 'light' | 'dark';

/** Width / height of the lockup SVGs (1800 x 820). */
const LOCKUP_RATIO = 1800 / 820;

export interface LogoProps {
  variant?: LogoVariant;
  tone?: LogoTone;
  /** Rendered height in px. */
  height?: number;
  className?: string;
  /**
   * True when the brand name is visible right next to the logo (the image then
   * has an empty alt and is hidden from assistive technology).
   */
  decorative?: boolean;
}

const DEFAULT_HEIGHT: Record<LogoVariant, number> = {
  lockup: 160,
  wordmark: 40,
  icon: 40,
};

/**
 * The Gharamiat logo. Lockup and icon are the SVG files in `public/brand`
 * (shown with <img>), the wordmark is inline SVG. Discreet mode never shows
 * the brand: a neutral "Notes" text takes its place.
 */
export const Logo: React.FC<LogoProps> = ({
  variant = 'lockup',
  tone = 'auto',
  height = DEFAULT_HEIGHT[variant],
  className,
  decorative = false,
}) => {
  const discreet = useDiscreet();
  const name = useBrandName();

  if (discreet) {
    return (
      <span className={className} data-logo="discreet">
        {DISCREET_TITLE}
      </span>
    );
  }

  if (variant === 'wordmark') {
    return (
      <span
        className={className ?? 'inline-block'}
        style={{ color: BRAND_COLORS.lipstick }}
      >
        <Wordmark height={height} label={name} decorative={decorative} />
      </span>
    );
  }

  const src =
    variant === 'icon'
      ? BRAND_LOGO.icon
      : tone === 'light'
        ? BRAND_LOGO.light
        : BRAND_LOGO.dark;
  const width = variant === 'icon' ? height : Math.round(height * LOCKUP_RATIO);

  return (
    <img
      src={src}
      width={width}
      height={height}
      alt={decorative ? '' : name}
      aria-hidden={decorative ? true : undefined}
      draggable={false}
      className={['h-auto max-w-full', className].filter(Boolean).join(' ')}
      data-logo={variant}
      data-tone={
        variant === 'lockup' ? (tone === 'light' ? 'light' : 'dark') : undefined
      }
    />
  );
};

export default Logo;
